<template>
  <div v-if="autoCampaign" class="medium-3 columns" id="settings-wrapper">
    <modal
        v-if="showMilestoneModal"
        wrapperClasses="milestone-modal">
        <h2 v-if="dateSelected" slot="header" class="title">setting a milestone start date</h2>
        <h2 v-else slot="header" class="title">setting a milestone to 'all time'</h2>

        <div slot="body" class="body">
          <p v-if="dateSelected">adding a milestone start date will only count transactions of members going forward from the defined start date. once a start date is defined, changing the milestone to “all time” may cause this autoconnect to send to members who have previously qualified in the past.</p>
          <p v-else>setting your milestone to “all time” will count transactions of members retroactively and apply them to the milestone. if a member has already achieved this milestone in the past, once enabled, the member will be sent this autoconnect on their next transaction</p>
        </div>

        <div
            slot="action"
            class="button larger modal-click-action"
            @click="showMilestoneModal = false">
          got it
        </div>
    </modal>

    <modal
      v-if="showBrandSelectionModal"
      isWarningModal
      showClose
      warningCloseText="no"
      warningProceedText="yes"
      wrapperClasses="brand-selection-modal"
      @close="deselectBrand"
      @proceed="setBrand">
      <template #body>
        <p class="body">are you sure you would like to select {{brandsData.selected.label}}? once you select a brand you will no longer be able to deselect it</p>
      </template>
    </modal>

    <div class="data-description">configure autoconnect settings</div>
    <small class="error-label in-settings-title" v-if="apiError">{{apiError}}</small>
    <div id="mobile-wrapper">
      <label>status</label>
      <div id="switch-wrapper">
        <span v-if="isEnabled">enabled</span>
        <span v-else>disabled</span>
        <span class="switch">
            <input name="auto_campaign[enabled]" type="hidden" value="0">
            <input id="enabled"
                   class="switch-input"
                   type="checkbox"
                   value="1"
                   name="auto_campaign[enabled]"
                   v-model="isEnabled"
                   @change="toggleEnable" />
            <label class="switch-paddle enable-button" for="enabled"></label>
          </span>
      </div>

      <div v-if="!isBrandFollowUpType">
        <label id="name-label" for="name">name ({{inputTitle}})</label>
        <input autofocus="autofocus"
              :class="['form-control', {['error-label']: errors.name}]"
              id="name"
              name="auto_campaign_name"
              type="text"
              v-model="nameValue"
              @blur="setInteractions('name')"
              @change="validateName" />
        <small class="error-label" v-if="errors.name">{{errors.name}}</small>
      </div>

      <div v-if="isAbandonedCartType" class="setting-wrapper abandoned-cart-dropdown">
        <label>location</label>
        <sb-async-dropdown
          :class="{ deselected: !abandonedCartLocations.selected.length }"
          :rows="abandonedCartLocations.rows"
          :header-rows="abandonedCartLocations.headerRows"
          :footer-rows="abandonedCartLocations.footerRows"
          :focused.sync="abandonedCartLocations.focused"
          :selected.sync="abandonedCartLocations.selected"
          :options="abandonedCartLocations.options"
          @selectedRow="updateLocationSelection"
          @deselectedRow="updateLocationSelection"
          style="z-index:2"
        ></sb-async-dropdown>

        <div class="data-disclaimer my-0">
          <p class="mt-4">This autoconnect will send to customers who have abandoned a cart at any of the selected locations. If no locations are selected, the autoconnect will behave as if all locations have been selected.</p>
          <p class="mt-2">If multiple autoconnects are enabled for the same location, the customer will receive multiple messages.</p>
        </div>
      </div>

      <div v-if="settings" :class="['auto-campaign-settings', settings.type]">
        <label v-if="settings.inputItem && settings.inputItem.label"
               :for="settings.inputItem.for">{{settings.inputItem.label}}</label>
        <select-toggle-input v-if="settings.type === 'referral'"
                             :inputItem="settings.toggleInput"
                             :openToggleValue ="settings.openValue"
                             :selectBoxInput="settings.selectBox"
                             shouldSetInteractions
                             showErrors
                             zeroAllowed
                             @saveInputValue="updateSettings"
                             @setErrors="setErrors"
                             @setInteractions="setInteractions"></select-toggle-input>

        <toggle-radio-input v-if="settings.toggleRadio"
                            :inputId="settings.toggleRadio.inputId"
                            :radios="settings.toggleRadio.radios"
                            :savedInputValue="autoCampaign[settings.toggleRadio.inputId]"
                            :spanBeforeText="settings.toggleRadio.spanBeforeText"
                            :spanAfterText="settings.toggleRadio.spanAfterText"
                            :valueLabel="settings.toggleRadio.valueLabel"
                            @setErrors="setErrors"
                            @setInteractions="setInteractions"
                            @updateSettings="updateSettings"></toggle-radio-input>

        <input-item v-if="settings.inputItem"
                    :inputClass="settings.inputItem.class"
                    :inputId="settings.inputItem.for"
                    :inputType="settings.inputItem.inputType"
                    :inputValue="autoCampaign[settings.inputItem.for]"
                    :isCurrency="settings.inputItem.isCurrency"
                    :options="settings.inputItem.options"
                    :placeholder="settings.inputItem.placeHolder"
                    :step="settings.inputItem.inputStep"
                    :min="settings.inputItem.min"
                    :max="settings.inputItem.max"
                    shouldSetInteractions
                    showErrors
                    @setErrors="setErrors"
                    @saveInputValue="updateSettings"
                    @setInteractions="setInteractions"></input-item>

        <min-max-inputs v-if="settings.hasMinMax"
                        :maxInput="settings.maxInput"
                        :minInput="settings.minInput"
                        shouldSetInteractions
                        showErrors
                        @setErrors="setErrors"
                        @saveInputValue="updateSettings"
                        @setInteractions="setInteractions"></min-max-inputs>

        <div v-if="settings.setBrand" class="setting-wrapper">
          <label>brand</label>
          <sb-async-dropdown
            :class="[{disabled: hasSelectedBrand}, {error: errors.brand}]"
            :rows="brandsData.rows"
            :header-rows="brandsData.headerRows"
            :footer-rows="brandsData.footerRows"
            :focused.sync="brandsData.focused"
            :selected.sync="brandsData.selected"
            :options="brandsData.options"
            v-on:update:selected="updateSelectedBrand"
            v-on:update:focused="updateBrandInteraction"
            style="z-index:2"
          ></sb-async-dropdown>
          <small class="error-label" v-if="errors.brand">{{errors.brand}}</small>
        </div>

        <div v-if="settings.setFrequency" class="setting-wrapper">
          <label>frequency</label>
          <sb-dropdown
            :class="{error: errors.frequency}"
            :selected.sync="selectedFrequency"
            :items="['weekly', 'every other week', 'monthly']"
            v-on:update:selected="updateSelectedFrequency"
            v-on:update:focused="updateFrequencyInteraction"
          ></sb-dropdown>
          <small class="error-label" v-if="errors.frequency">{{errors.frequency}}</small>
        </div>

        <div v-if="settings.setDateRange" class="setting-wrapper">
          <label>date range</label>
          <sb-date-range
            :class="{error: errors.dateRange}"
            :start.sync="selectedStartDateRange"
            :end.sync="selectedEndDateRange"
            :flatpickr="dateRangeFlatpickrConfig"
            v-on:update:range="updateSelectedDateRange"
            format="M-d-yy"
          ></sb-date-range>
          <small class="error-label" v-if="errors.dateRange">{{errors.dateRange}}</small>
        </div>

        <!-- This does not need interaction/error handling since it has a default value -->
        <!-- and after that user cannot de-selects it -->
        <div v-if="settings.setSendDay" class="setting-wrapper">
          <label>send day of the week</label>
          <sb-dropdown
            :selected.sync="selectedWeekDay"
            :items="weekDays"
            v-on:update:selected="updateSelectedWeekDay"
          ></sb-dropdown>
        </div>

        <send-time-input v-if="settings.setTime"
                         :sendAtLocal="autoCampaign.send_at_local"
                         :autoCampaignType="this.autoCampaign.type"
                         @setInteractions="setInteractions"
                         @updateSettings="updateSettings"></send-time-input>

       <div v-if="settings.disclaimer" class="data-disclaimer">
         Disclaimer: {{settings.disclaimer}}
       </div>

        <div v-if="settings.type === 'after-visit' && hasLocations">
          <label>transaction location based after visits?</label>
          <sb-tab
            class="transaction-location-tabs"
            :tabs="transactionTabs"
            :selected.sync="selectedTransaction"
            @update:selected="updatedTransaction($event)" />
        </div>

        <add-configuration v-if="settings.hasAddConfiguration"
                           :shouldOpenOnCreate="!!autoCampaign.argument_2 || !!autoCampaign.argument_3">
          <div slot="configItems" class="config-items-slot">
            <input-item :inputClass="settings.timeframeInput.class"
                        :inputId="settings.timeframeInput.for"
                        :inputType="settings.timeframeInput.inputType"
                        :inputValue="autoCampaign[settings.timeframeInput.for]"
                        :labelText="settings.timeframeInput.label"
                        showErrors
                        @setErrors="setErrors"
                        @saveInputValue="updateSettings"
                        @setInteractions="setInteractions"></input-item>

            <input-item :inputClass="settings.circumventSelect.class"
                        :inputId="settings.circumventSelect.for"
                        :inputType="settings.circumventSelect.inputType"
                        :inputValue="circumventSelectValue"
                        :labelText="settings.circumventSelect.label"
                        :options="settings.circumventSelect.options"
                        showErrors
                        @setErrors="setErrors"
                        @saveInputValue="updateSettings"
                        @setInteractions="setInteractions"></input-item>

            <div  v-if="settings.type === 'spend-milestone'">
              <label class="milestone-label">milestone start date</label>
              <div class="milestone-tabs">
                <div @click="selectedMilestone('all')"
                      :class="['all', 'milestone', { selected: allSelected}]">all time</div>
                <div @click="selectedMilestone('date')"
                      :class="['date', 'milestone', { selected: dateSelected}]">date</div>
              </div>
            </div>

            <flat-pickr v-if="dateSelected"
                        name="date"
                        v-model="date"
                        :config="datePickerConfig"
                        @on-close="updateMilestoneDate"></flat-pickr>
          </div>
        </add-configuration>
      </div>
    </div>
  </div>
</template>


<script>
  import _ from 'lodash'
  import AddConfiguration from './AddConfiguration'
  import axiosConfig from '../../config/axios'
  import ConfigureSettings from '../../mixins/ConfigureSettingsMixin'
  import flatPickr from 'vue-flatpickr-component'
  import InputItem from '../FormItems/InputItem'
  import moment from 'moment'
  import MinMaxInputs from '../FormItems/MinMaxInputs'
  import Modal from '../../components/shared/Modal'
  import SelectToggleInput from '../FormItems/SelectToggleInput'
  import SendTimeInput from './SendTimeInput'
  import ToggleRadioInput from '../FormItems/ToggleRadioInput'
  import { autoCampaignAPI } from '../../services/api/autoCampaignAPI'
  import { format, set, startOfTomorrow } from 'date-fns'
  import { pluralize } from '../../utilities'

  export default {
    props: {
      allowEnable: Boolean,
      autoCampaignId: String,
      autoCampaignFromEdit: Object,
      otherErrors: Object,
      contactType: String,
    },

    name: 'Settings',

    components: {
      InputItem,
      AddConfiguration,
      MinMaxInputs,
      Modal,
      SelectToggleInput,
      SendTimeInput,
      ToggleRadioInput,
      flatPickr
    },

    mixins: [ConfigureSettings],

    data() {
      return {
        addConfiguration: false,
        apiError: null,
        allSelected: true,
        autoCampaign: null,
        datePickerConfig: {
          altFormat: 'Y-m-d',
          dateFormat: 'D, M d, Y'
        },
        date: null,
        dateSelected: false,
        inputTitle: this.contactType,
        errors: {},
        interactions: {
          name: true,
          brand: false,
        },
        isEnabled: false,
        nameValue: null,
        showMilestoneModal: false,
        brandsData: {
          rows: ["loading-row"],
          selected: {},
          focused: false,
          headerRows: [],
          footerRows: [],
          options: {
            label(selected) {
              return Object.keys(selected)?.length > 0 ? selected.label : "nothing selected"
            }
          }
        },
        abandonedCartLocations: {
          rows: ["loading-row"],
          selected: [],
          focused: false,
          headerRows: [],
          footerRows: [],
          options: {
            label(selected) {
              return selected.length ? `${selected.length} ${pluralize(selected.length, 'location')}` : 'select a location'
            }
          },
        },
        weekDays: ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"],
        currentBrandPage: 0,
        selectedBrand: [],
        selectedFrequency: null,
        selectedStartDateRange: null,
        selectedEndDateRange: null,
        selectedWeekDay: null,
        showBrandSelectionModal: false,
        debounceBrandSearch: null,
        transactionTabs: [
          { key: "yes", label: "yes" },
          { key: "no", label: "no" }
        ],
        selectedTransaction: null,
      }
    },

    methods: {
      updateLocationSelection(state) {
        this.updateSettings({ auto_campaign_locations_attributes:
          [{
            merchant_location_id: state.id,
            _destroy: !this.abandonedCartLocations.selected.some(location => location.id === state.id)
          }]
        })
      },

      async fetchAbandonedCartLocations({currentPage = 0, searchText}) {
        this.debouncedLocationSearch = _.debounce(this.fetchAbandonedCartLocations, 500)
        const params = {
            page: currentPage + 1,
            ...(searchText && { query: searchText })
          }

        try {
          const locations = await autoCampaignAPI.getAbandonedCartLocations(params)
          this.setAbandonedCartLocations(locations, currentPage, searchText)
        } catch (error) {
          this.apiError = error
          this.abandonedCartLocations.rows = []
        }
      },

      setAbandonedCartLocations(locations, currentPage = 1, searchText = "") {
        const queryRow = this.setQueryRow(searchText, this.debouncedLocationSearch)
        const paginationRow = this.setPaginationRow(currentPage, locations.meta.total_pages, this.fetchAbandonedCartLocations)
        let locationRows

        if (locations?.merchant_locations?.length > 0) {
          locationRows = locations.merchant_locations.map(({ label, id }) => ({
            slot: "toggle-row",
            state: { label, id }
          }))
        } else {
          locationRows = [{
            slot: 'text-row',
            state: {
              message: searchText ? 'no search results' : 'no locations enabled'
            }
          }]
        }

        this.abandonedCartLocations.headerRows = [queryRow]
        this.abandonedCartLocations.rows = locationRows
        this.abandonedCartLocations.footerRows = [paginationRow]
      },

      updateFrequencyInteraction(focused) {
        if (!focused) {
          this.interactions.frequency = !this.selectedFrequency
          this.updateInteractions()
        }
        this.setErrors('frequency', !focused && !this.selectedFrequency ? "select a frequency" : null)
      },

      updateBrandInteraction(focused) {
        const hasSelectedBrand = Object.keys(this.brandsData.selected).length > 0
        if (!focused) {
          this.interactions.brand = !hasSelectedBrand
          this.updateInteractions()
        }
        this.setErrors('brand', !focused && !hasSelectedBrand ? "select a brand" : null)
      },

      async fetchBrandsData({currentPage = 0, searchText}) {
        this.brandsData.headerRows = []
        this.brandsData.footerRows = []
        this.brandsData.rows = ["loading-row"]
        this.debounceBrandSearch = _.debounce(this.fetchBrandsData, 500)

        try {
          const brands = await autoCampaignAPI.getBrands(currentPage + 1, searchText)
          this.setBrandsData(brands, currentPage, searchText)
        } catch (e) {
          this.apiError = e
          this.brandsData.rows = []
        }
      },

      setBrandsData(brands, currentPage, searchText) {
        const queryRow = this.setQueryRow(searchText, this.debounceBrandSearch)
        const paginationRow = this.setPaginationRow(currentPage, brands.pagination.total_pages, this.fetchBrandsData)
        let brandRows

        if (brands?.brands?.length > 0) {
          brandRows = brands.brands.map(brand => ({
            slot: "select-row",
            state: {
              id: brand.id,
              label: brand.name
            }
          }))
        } else {
          brandRows = [{
            slot: 'text-row',
            state: {
              message: searchText ? 'no search results' : 'no brands available'
            }
          }]
        }

        this.brandsData.headerRows = [queryRow]
        this.brandsData.rows = brandRows
        this.brandsData.footerRows = [paginationRow]
      },

      updateSelectedBrand() {
        this.showBrandSelectionModal = true
      },

      setBrand() {
        this.showBrandSelectionModal = false
        this.updateSettings({ advertiser_id: this.brandsData.selected.id })
      },

      deselectBrand() {
        this.showBrandSelectionModal = false
        this.brandsData.selected = {}
      },

      setQueryRow(searchText, debouncedSearchFunction) {
        const queryRow = {
          slot: "query-row",
          state: {
            value: searchText || "",
            update: (newValue) => {
              queryRow.state.value = newValue
              debouncedSearchFunction({searchText: queryRow.state.value})
            }
          }
        }

        return queryRow
      },

      setPaginationRow(currentPage, totalPages, fetchDataFunction) {
        const paginationRow = {
          slot: "pagination-row",
          state: {
            page: currentPage,
            pages: totalPages,
            update: (newPage) => {
              fetchDataFunction({currentPage: newPage})
            }
          }
        }

        return paginationRow
      },

      updateSelectedWeekDay() {
        this.updateSettings({ argument_4: this.selectedWeekDay})
      },

      updateSelectedFrequency() {
        this.updateSettings({ argument_3: this.selectedFrequency})
      },

      updateSelectedDateRange(selectedRange) {
        let error = null
        if (!!selectedRange.start && !!selectedRange.end) {
          this.interactions.dateRange = false

          const dataToUpdate = {
            argument_1: format(set(selectedRange.start, { hours: 9 }), "y-M-d HH:mm"),
            argument_2: format(set(selectedRange.end, { hours: 17 }), "y-M-d HH:mm")
          }
          this.updateInteractions()
          this.updateSettings(dataToUpdate)
        } else {
          error = "select a date range"
        }

        this.setErrors('dateRange', error)
      },

      disableIfError(dataToUpdate) {
        if (dataToUpdate.enabled) { this.isEnabled = false }
      },

      initInteractions() {
        if (this.settings) {
          if (this.settings.setTime) { this.interactions.time = !this.autoCampaign.send_at_local }
          if (this.settings.interactionsToSet) {
            this.settings.interactionsToSet.forEach((argItem) => {
              this.$set(this.interactions, argItem, !this.autoCampaign[argItem])
            })
          }
        }
      },

      initSettings() {
        this.nameValue = this.autoCampaign.name
        this.$set(this.interactions, 'name', this.isBrandFollowUpType ? false : !this.nameValue)

        this.configureSettings()
        this.configureBrandFollowUp()
        this.initInteractions()
      },

      configureBrandFollowUp() {
        if (this.isBrandFollowUpType) {
          this.$set(this.interactions, 'brand', !this.autoCampaign.advertiser_id)
          this.$set(this.interactions, 'frequency', !this.autoCampaign.argument_3)
          this.$set(this.interactions, 'dateRange', !this.autoCampaign.argument_1 && !this.autoCampaign.argument_2)

          if (this.autoCampaign.advertiser_id) {
            this.brandsData.selected = {
              id: this.autoCampaign.advertiser_id,
              label: this.autoCampaign.advertiser_name
            }
          }

          const startDate = this.autoCampaign.argument_1,
                endDate = this.autoCampaign.argument_2

          this.selectedFrequency = this.autoCampaign.argument_3
          if (startDate) this.selectedStartDateRange = new Date(startDate)
          if (endDate) this.selectedEndDateRange = new Date(endDate)
          this.selectedWeekDay = this.autoCampaign.argument_4?.toLowerCase()
        }
      },

      setErrors(errorsKey, errorMessage) {
        const errorItem = this.errors[errorsKey]
        if (errorMessage) {
          if (errorItem !== errorMessage || !errorItem) { this.$set(this.errors, errorsKey, errorMessage) }
        } else {
          if (errorItem) { this.$delete(this.errors, errorsKey) }
        }
      },

      setInteractions(settingKey, fromInput) {
        if (this.interactions[settingKey]) {
          this.$set(this.interactions, settingKey, false)
          if (settingKey === 'name' && !fromInput) { this.validateName(null, true) }
          this.updateInteractions()
        }
      },

      toggleEnable(event) {
        let shouldEnable = event ? event.target.checked : false
        if(typeof event === 'boolean') {
          this.isEnabled = event;
        }
        if (shouldEnable !== this.autoCampaign.enabled) { this.updateSettings({ enabled: shouldEnable }) }
      },

      updateInteractions() {
        const updateInteractions = Object.values(this.interactions).includes(true) ? { settings: true } : {}
        this.$store.dispatch('UPDATE_STATE_OBJECT', {
          stateKey: 'noUserInteractions',
          itemKey: 'settings',
          itemObject: updateInteractions
        })
      },

      updateMilestoneDate(date) {
        let stripTimeZone = moment(date[0]).format().split('T')
        this.updateSettings({ argument_4: stripTimeZone[0]})
      },

      updateSettings(dataToUpdate, isFromBlur) {
        if (!isFromBlur) {
          const url = `/api/v3/auto_campaigns/${this.autoCampaign.id}`,
            campaignData = { auto_campaign: dataToUpdate }
          axiosConfig.patch(url, campaignData)
            .then((response) => {
              const data = response?.data?.auto_campaign,
                errorMessage = response?.data?.error || response?.data?.errors?.reject_reason?.[0]
              if (data && !errorMessage) {
                this.autoCampaign = data
                this.$emit('setAutoCampaign', this.autoCampaign)
                this.apiError = null
              } else if (errorMessage) {
                this.apiError = `cannot update settings: ${errorMessage}`
                this.disableIfError(dataToUpdate)
              } else {
                this.apiError = 'issue encountered updating settings'
                this.disableIfError(dataToUpdate)
              }
            })
            .catch((error) => {
              this.apiError = `issue encountered updating settings: ${error}`
              this.disableIfError(dataToUpdate)
            })
        }
      },

      updatedTransaction(transaction){
        this.updateSettings({ argument_1: transaction.key });
      },

      setTitle() {
        const walletTypes = ['email add', 'interests add', 'birthday add', 'spend milestone']
        this.inputTitle = walletTypes.includes(this.autoCampaign.header) ? 'will display on wallet' : 'for your reference'
      },

      validateName(event, isFromBlur) {
        this.setInteractions('name', true)
        const name = event?.currentTarget?.value || this.autoCampaign.name
        if (name) {
          this.$delete(this.errors, 'name')
          if (this.autoCampaign.name !== name) { this.updateSettings({ name: name }, isFromBlur) }
        } else {
          this.$set(this.errors, 'name', 'autoconnect must have a name')
        }
      },

      validateSettings(isValid) {
        const validObject = isValid ? {} : { settings: isValid }
        this.$store.dispatch('UPDATE_STATE_OBJECT', {itemKey: 'settings', itemObject: validObject})
      },

      selectedMilestone(milestone) {
        this.showMilestoneModal = true

        if (milestone === 'all') {
          this.updateSettings({ argument_4: 'all time' })
          this.allSelected = true
          this.dateSelected = false
        } else {
          this.dateSelected = true
          this.allSelected = false
        }
      },

      // This will take in argument_4 value and argument_4 isn't always a date string
      formatToDate(date) {
        if (isNaN(Date.parse(date))) return
        this.date = format(new Date(date),'EEE, MMM do, YYYY')
      }
    },

    created() {
      this.autoCampaign = this.autoCampaignFromEdit
      this.isEnabled = this.autoCampaign.enabled
      if(this.autoCampaign?.type === 14 && this.autoCampaign?.argument_1) {
        this.selectedTransaction = this.transactionTabs.find(tab => tab.key === this.autoCampaign.argument_1);
      }else{
        this.selectedTransaction = this.transactionTabs[1]
      }

      this.initSettings()
      this.updateInteractions()
      this.setTitle()
      this.fetchBrandsData({})

      if (this.autoCampaign?.type === 19) {
        this.abandonedCartLocations.selected = this.autoCampaign.locations.map(location => {
          return { label: location.label, id: location.merchant_location_id }
        })
        this.fetchAbandonedCartLocations({})
      }

      if (this.autoCampaign?.argument_4 !== null) {
        this.formatToDate(this.autoCampaign.argument_4)
        this.dateSelected = true
        this.allSelected = false
      } else {
        this.allSelected = true
      }
    },

    computed: {
      circumventSelectValue: function() {
        if (this.settings?.hasAddConfiguration) {
          const selectValue = this.autoCampaign[this.settings.circumventSelect.for]
          return selectValue ? selectValue : ''
        }
      },

      isBrandFollowUpType() {
        return this.autoCampaign.type === 20
      },

      isAbandonedCartType() {
        return this.autoCampaign.type === 19
      },

      hasLocations() {
        return !!this.autoCampaign.scopes[0].groups_with_selected.find(segment => segment.name === 'locations');
      },

      hasSelectedBrand() {
        return Object.keys(this.brandsData.selected)?.length > 0
      },

      dateRangeFlatpickrConfig() {
        return { minDate: startOfTomorrow() }
      },

      brandsDropdownOptions() {
        return {
          label(selected) {
            return this.selectedselected?.length > 0 ? selected[0].label : "nothing selected"
          }
        }
      }
    },

    watch: {
      errors: function() {
        this.validateSettings(!Object.keys(this.errors).length)
      }
    },
  }
</script>

<style scoped>
#settings-wrapper .brand-selection-modal >>> .modal {
  padding: .75em 2em;
  width: 100%;
}

#settings-wrapper .brand-selection-modal >>> .warning-wrapper {
  justify-content: center;
}

#settings-wrapper .brand-selection-modal >>> .action-button {
  font-size: 1em;
  margin: 0 1em;
  width: 90px;
}

#settings-wrapper .setting-wrapper {
  margin-bottom: 1rem;
}

/* Need to use !important to override the style from parent AutoCampaignEdit */
/* This only applies for those components from vue-components */
#settings-wrapper .setting-wrapper .error-label {
  margin: unset !important;
}

#settings-wrapper >>> .sb-base-dropdown {
  margin-bottom: 0;
  width: 100%;
}

#settings-wrapper >>> .sb-base-dropdown .sb-content {
  max-width: 100%;
}

#settings-wrapper >>> .sb-base-dropdown .label-container {
  line-height: 1.5;
}

#settings-wrapper >>> .sb-base-dropdown .label-container .sb-label {
  padding: 8px 10px;
}

#settings-wrapper >>> .sb-base-dropdown .label-container .flex {
  justify-content: space-between;
  width: 100%;
}

#settings-wrapper >>> .sb-base-dropdown.error main.label-container {
  border: 2px solid #f91253;
}

#settings-wrapper >>> .sb-base-dropdown.disabled {
  pointer-events: none;
}

#settings-wrapper >>> .sb-base-dropdown.disabled main.label-container {
  background-color: #a4a4a4;
}

#settings-wrapper >>> .sb-base-dropdown *,
#settings-wrapper >>> .sb-dropdown *,
#settings-wrapper >>> .sb-date-range * {
  font-size: 1rem;
}

#settings-wrapper >>> .sb-multi-dropdown .query-container .query {
  height: max-content;
  margin-bottom: 5px;
  padding: 5px 10px;
}

#settings-wrapper >>> .sb-dropdown {
  margin: 0;
  width: 100%;
}

#settings-wrapper >>> .sb-dropdown > * {
  font-family: 'AileronThin';
}

#settings-wrapper >>> .sb-dropdown .icon-container {
  margin-right: 0;
}

#settings-wrapper >>> .sb-dropdown .label-container {
  min-width: unset;
  width: 100%;
}

#settings-wrapper >>> .sb-date-range {
  width: 100%;
}

#settings-wrapper >>> .sb-date-range .label-container {
  padding: 5px 10px;
}

.transaction-location-tabs >>> .item .label{
  background-color: #fff;
  padding: 0;
  height: 2.4375rem;
  border-radius: 8px;
}

.transaction-location-tabs >>> .item.is-active:first-child .label{
  border-radius: 5px 0 0 5px!important;
}

.transaction-location-tabs >>> .item.is-active:last-child .label{
  border-radius: 0 5px 5px 0!important;
}

.transaction-location-tabs >>> .item.is-active .label{
  background-color: #128ff9!important;
  color: #fff!important;
}

.abandoned-cart-dropdown >>> .sb-base-dropdown.deselected .sb-content {
  border-top: 1px solid transparent;
}

.abandoned-cart-dropdown >>> .sb-base-dropdown.deselected .label-container .sb-label {
  color: #128ff9;
  background-color: white;
  border: 1px solid #128ff9;
}
</style>
